import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import { createApp } from 'vue'

import App from './App.vue'
import { router } from './router'
import { useAuth } from './stores/auth'
import './styles.css'
import 'primeicons/primeicons.css'

/**
 * Blue primary, neutral chrome.
 *
 * Primary is the one action per screen (Save, Create) and the active nav item;
 * green, amber and red are kept for state (live, pending, failing) so they read
 * at a glance. Surfaces are Aura's zinc-like slate in both themes.
 */
const AdCrew = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}', 100: '{blue.100}', 200: '{blue.200}', 300: '{blue.300}',
      400: '{blue.400}', 500: '{blue.500}', 600: '{blue.600}', 700: '{blue.700}',
      800: '{blue.800}', 900: '{blue.900}', 950: '{blue.950}',
    },
    colorScheme: {
      light: {
        primary: { color: '{blue.600}', hoverColor: '{blue.700}', activeColor: '{blue.800}' },
        surface: {
          0: '#ffffff', 50: '{slate.50}', 100: '{slate.100}', 200: '{slate.200}', 300: '{slate.300}',
          400: '{slate.400}', 500: '{slate.500}', 600: '{slate.600}', 700: '{slate.700}',
          800: '{slate.800}', 900: '{slate.900}', 950: '{slate.950}',
        },
      },
      dark: {
        primary: { color: '{blue.400}', hoverColor: '{blue.300}', activeColor: '{blue.200}' },
        surface: {
          0: '#ffffff', 50: '{slate.50}', 100: '{slate.100}', 200: '{slate.200}', 300: '{slate.300}',
          400: '{slate.400}', 500: '{slate.500}', 600: '{slate.600}', 700: '{slate.700}',
          800: '{slate.800}', 900: '#111a2e', 950: '#0b1220',
        },
      },
    },
  },
  components: {
    tabs: {
      tablist: { background: 'transparent' },
      tabpanel: { background: 'transparent', padding: '1.25rem 0 0 0' },
    },
  },
})

const app = createApp(App)

app.use(createPinia())
useAuth().install(router)
app.use(router)
app.use(PrimeVue, {
  theme: {
    preset: AdCrew,
    options: {
      darkModeSelector: '.dark',
      cssLayer: { name: 'primevue', order: 'theme, base, primevue, app' },
    },
  },
  ripple: false,
  // PrimeVue 5 is licensed; without a key every page shows a license badge.
  // Set at build from VITE_PRIMEUI_LICENSE (the exchange console uses the same).
  license: import.meta.env.VITE_PRIMEUI_LICENSE || undefined,
})
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

app.mount('#app')
